import type {
  LiquidActivity,
  DeepPartial,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { LiquidUnit } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
  Popover,
  PopoverTrigger,
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuItemSeparator,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  Fragment,
  useCallback,
} from "react"

import { inputPropertiesByUnit } from "./inputPropertiesByUnit"
import { useDefaultLiquidUnit } from "./useDefaultLiquidUnit"
import { usePolyglot } from "src/contexts"
import { useSliderInputProps } from "src/hooks"
import styles from "src/styles/entryModal.module.scss"
import {
  liquidMicrolitersToUnit,
  liquidUnitToMicroliters,
} from "src/utils"

const polyglotPrefix = "components.entry_modal.liquid.unit."

interface Props {
  etnits?: string;
  activity?: DeepPartial<LiquidActivity>;
  updateActivity: (a: DeepPartial<LiquidActivity>) => void;
  readOnly?: boolean;
}

const id = "liquid-units-range-input"

const units: Array<LiquidUnit> = [
  LiquidUnit.Milliliters,
  LiquidUnit.Liters,
  LiquidUnit.FluidOunces,
  LiquidUnit.Cups,
  LiquidUnit.Pints,
]

/*
 * renders a number label and range input for choosing number of units
 */
export function LiquidRangeInput(props: Props) {
  const {
    activity,
    updateActivity,
    readOnly = false,
  } = props

  const polyglot = usePolyglot()
  const defaultLiquidUnit = useDefaultLiquidUnit()

  const updateUnit = useCallback(
    (unit: LiquidUnit) => {
      updateActivity({ units: unit })
    },
    [updateActivity],
  )

  const {
    sliderStep,
    inputStep,
    max,
    min,
  } = inputPropertiesByUnit[activity?.units ?? defaultLiquidUnit]

  const onChange = useCallback(
    (numUnits: number) => {
      updateActivity({
        units: activity?.units ?? defaultLiquidUnit,
        microliters: liquidUnitToMicroliters(
          numUnits,
          activity?.units ?? defaultLiquidUnit,
        ),
      })
    },
    [
      activity,
      updateActivity,
      defaultLiquidUnit,
    ],
  )

  const value = activity?.microliters !== undefined
    ? liquidMicrolitersToUnit(
      activity.microliters,
      activity?.units ?? defaultLiquidUnit,
    )
    : null

  const {
    inputValue,
    sliderValue,
    onNumberChange,
    onSliderChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    min,
    max,
    inputStep,
    sliderStep,
    value,
    onChange,
    allowEmptyValue: true,
  })

  return (
    <div className={styles.row}>
      <SliderValue sizePreset={readOnly ? "large" : "largePopover"}>
        <SliderValueInput
          max={max}
          min={min}
          step={inputStep}
          value={inputValue}
          required
          readOnly={readOnly}
          id="liquid-number-range"
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          onKeyDown={onNumberKeyDown}
        />
        <SliderValueLabelContainer>
          {readOnly ? (
            <SliderValueLabel
              htmlFor={id}
            >
              {polyglot.t(`${polyglotPrefix}${activity?.units ?? defaultLiquidUnit}`)}
            </SliderValueLabel>
          ) : (
            <Popover placement="top-start">
              <PopoverTrigger asChild>
                <SliderValueLabel
                  htmlFor={id}
                  clickable
                >
                  {polyglot.t(`${polyglotPrefix}${activity?.units ?? defaultLiquidUnit}`)}
                </SliderValueLabel>
              </PopoverTrigger>
              <PopoverMenu
                aria-label={polyglot.t(`${polyglotPrefix}aria_label`)}
              >
                {units.map((unit: LiquidUnit, index: number) => {
                  const onClick = () => updateUnit(unit)

                  return (
                    <Fragment key={unit}>
                      {index !== 0 && <PopoverMenuItemSeparator />}
                      <PopoverMenuItem
                        onClick={onClick}
                        autoDismiss
                      >
                        {polyglot.t(`${polyglotPrefix}${unit}`)}
                      </PopoverMenuItem>
                    </Fragment>
                  )
                })}
              </PopoverMenu>
            </Popover>
          )}
        </SliderValueLabelContainer>
      </SliderValue>
      {!readOnly && (
        <Slider className={styles.rangeInputSlider}>
          <SliderInput
            min={min}
            max={max}
            step={sliderStep}
            id={id}
            value={sliderValue ?? min}
            onChange={onSliderChange}
            required
          />
          <SliderTracks
            min={min}
            max={max}
          />
        </Slider>
      )}
    </div>
  )
}
